import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Calculator,
  KanbanSquare,
  Lock,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import OrgPlans from "@/components/OrgPlans";

interface LookupItem {
  id: number;
  name: string;
}

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Tell us what you're buying",
    body: "Set your criteria once — industry, budget, geography, involvement level. That's your Buy Box.",
  },
  {
    step: "02",
    title: "Get matched, not flooded",
    body: "AI deal scoring surfaces the handful of listings that actually fit, ranked against your profile.",
  },
  {
    step: "03",
    title: "Run the numbers and move",
    body: "Model SBA financing, track every conversation in your deal pipeline, and go from interest to offer in one place.",
  },
];

const TOOLS = [
  {
    icon: Calculator,
    eyebrow: "SBA Calculator",
    title: "Know your payment before you call a lender",
    body: "Loan amount, rate, term, down payment — a full amortization picture for any listing in seconds.",
  },
  {
    icon: KanbanSquare,
    eyebrow: "Deal Pipeline",
    title: "Every deal, every conversation, one board",
    body: "Track sellers, notes, documents, and next steps so nothing slips while you evaluate multiple targets.",
  },
  {
    icon: Sparkles,
    eyebrow: "AI Deal Matching",
    title: "Your shortlist, built for you",
    body: "New listings are scored against your criteria the moment they land — you hear about the fits first.",
  },
];

const HERO_PROOF = [
  {
    icon: ShieldCheck,
    title: "Verified inventory",
    body: "Every listing screened for real revenue and clean books before it goes live.",
  },
  {
    icon: TrendingUp,
    title: "AI deal scoring",
    body: "Each business scored against your criteria — price, cash flow, location, fit.",
  },
  {
    icon: Building2,
    title: "Financing built in",
    body: "Model SBA loans and deal structures without ever leaving the marketplace.",
  },
];

/** The org an organization landing page is rendering for. */
export interface LandingOrg {
  name: string;
  slug: string;
  headline: string | null;
  subheadline: string | null;
  description: string | null;
  tierNames: string[];
}

/**
 * The signed-out landing page. Serves BOTH "/" and every organization page at
 * "/<slug>" — one component, so the two can never drift apart and anything
 * added to the main page shows up on organization pages for free.
 *
 * Logged-in visitors never reach "/" — BrowseGuard forwards them to their role
 * home — so everything here is written for an anonymous viewer: real active
 * listings with the business name withheld (the public API redacts member
 * fields), plus the product story below.
 *
 * `org` is null on "/". When set, the page keeps every section and additionally
 * carries that organization's name, its own headline/subheadline where it has
 * them, and its plan pricing.
 *
 * `categories` comes from the parent so the teaser cards can resolve an
 * industry name without a second lookup request.
 */
export default function PublicLanding({
  org = null,
}: {
  /** Accepted but unused since the listing preview became account-only —
   *  kept so callers don't have to change. */
  categories?: LookupItem[];
  org?: LandingOrg | null;
}) {

  // Organization pages must send visitors to THAT organization's door: the
  // slug in the URL is what makes the new account a member of this org rather
  // than of BusinessLocating (see app/routers/auth.py::_resolve_org).
  const registerTo = org ? `/${org.slug}/register` : "/register";

  // Whether to point the hero's secondary CTA at the plans section. Answered
  // from the org record, so this page needs no tier request of its own —
  // OrgPlans fetches its own pricing.
  const hasPlans = (org?.tierNames.length ?? 0) > 0;
  return (
    <div className="public-stage">
      {/* ── Hero ───────────────────────────────────────────────────────────
          data-theme pins the Dark · Gold palette to this box in both themes,
          matching the signed-in hero. The inline transparent background undoes
          the [data-theme="dark"] rule's own background so only the rounded
          shell paints dark, not the full-width section behind it. */}
      <section data-theme="dark" style={{ backgroundColor: "transparent" }} className="px-4 pt-6 sm:px-6 lg:px-8">
        <div className="public-hero-shell relative w-full overflow-hidden rounded-[1.85rem] px-6 py-12 shadow-glow sm:px-8 sm:py-16">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          <div className="absolute -right-20 -top-16 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />

          {/* Full-bleed content — the page gutter is the only inset, so the
              headline lines up with the nav wordmark instead of floating in a
              centered column. Only the copy blocks cap their measure. */}
          <div className="relative z-10 w-full">
            <div className="public-pill">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {org ? org.name : "Private Business Marketplace"}
            </div>

            <h1 className="mt-6 max-w-4xl font-display text-4xl leading-[1.08] text-foreground sm:text-5xl lg:text-6xl">
              {org?.headline ? (
                org.headline
              ) : (
                <>
                  Off-market businesses for sale,{" "}
                  <span className="text-gradient">before anyone else sees them.</span>
                </>
              )}
            </h1>

            <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
              {org?.subheadline ??
                "Vetted listings with verified financials, AI deal scoring, and financing tools — built for operators who are actually buying, not browsing."}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link to={registerTo}>
                <Button size="lg" className="w-full shadow-glow sm:w-auto">
                  Get free access
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <a href={hasPlans ? "#plans" : "#listings"}>
                <Button size="lg" variant="outline" className="w-full border-primary/25 sm:w-auto">
                  {hasPlans ? "See membership plans" : "Preview the listings"}
                </Button>
              </a>
            </div>

            <div className="mt-12 grid gap-6 border-t border-border/60 pt-8 sm:grid-cols-3 sm:gap-10 xl:gap-16">
              {HERO_PROOF.map(({ icon: Icon, title, body }) => (
                <div key={title}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0 text-primary" />
                    <span className="text-sm font-bold text-foreground">{title}</span>
                  </div>
                  <p className="mt-1.5 text-[0.82rem] leading-6 text-muted-foreground">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Inside the vault — real active listings, names withheld ──────── */}
      <section
        id="listings"
        className="mt-14 scroll-mt-20 border-y border-border/60 bg-card/40 px-4 py-16 sm:px-6 lg:px-8"
      >
        <div className="w-full">
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">
                On the market now
              </div>
              <h2 className="mt-2.5 font-display text-3xl text-foreground sm:text-4xl">
                A look inside the vault
              </h2>
            </div>
            <div className="flex items-center gap-4">
                            <Link to="/register" className="text-sm font-semibold text-primary hover:text-primary-light">
                Register to see all listings →
              </Link>
            </div>
          </div>

          {/* Deal data is account-only. The catalogue used to be readable
              anonymously — all 726 listings with financials in 8 requests —
              so the preview is now a locked panel rather than live rows. */}
          <div className="mt-9 rounded-2xl border border-primary/25 bg-primary/[0.06] px-6 py-12 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/10">
              <Lock className="h-5 w-5 text-primary" />
            </span>
            <h3 className="mt-5 font-display text-2xl text-foreground">
              Deal details are for members
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-muted-foreground">
              Create a free account to browse every active listing — industry,
              location, revenue, EBITDA and asking price. No card required.
            </p>
            <Link to={registerTo} className="mt-6 inline-block">
              <Button size="lg" className="shadow-glow">
                Get free access
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <section id="how" className="scroll-mt-20 px-4 py-20 sm:px-6 lg:px-8">
        <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-primary">How it works</div>
        <h2 className="mt-2.5 max-w-lg font-display text-3xl text-foreground sm:text-4xl">
          From first look to letter of intent.
        </h2>

        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-10 xl:gap-16">
          {HOW_IT_WORKS.map(({ step, title, body }) => (
            <div key={step} className="border-t border-primary/25 pt-6">
              <div className="font-display text-5xl leading-none text-primary">{step}</div>
              <div className="mt-4 text-lg font-bold text-foreground">{title}</div>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Tools ─────────────────────────────────────────────────────────── */}
      <section id="tools" className="scroll-mt-20 border-y border-border/60 bg-card/40 px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid w-full gap-5 md:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map(({ icon: Icon, eyebrow, title, body }) => (
            <div
              key={eyebrow}
              className="hover-lift rounded-2xl border border-border/60 bg-card/70 p-7 transition-smooth hover:border-primary/40"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/25 bg-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </span>
              <div className="mt-5 text-[0.68rem] font-bold uppercase tracking-[0.18em] text-primary">
                {eyebrow}
              </div>
              <div className="mt-2.5 text-lg font-bold leading-snug text-foreground">{title}</div>
              <p className="mt-2.5 text-sm leading-6 text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Membership plans — organization pages only ────────────────────
          Shared with the custom organization landings in src/orgs/, so the
          pricing can never drift between them. "/" has no plans of its own,
          so OrgPlans renders nothing there. */}
      {org && <OrgPlans slug={org.slug} tierNames={org.tierNames} />}

      {/* ── Closing CTA ───────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden px-4 py-24 text-center sm:px-6">
        <div className="pointer-events-none absolute left-1/2 top-8 h-72 w-[34rem] -translate-x-1/2 rounded-full bg-primary/12 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-2xl">
          <h2 className="font-display text-3xl leading-tight text-foreground sm:text-[2.6rem]">
            The next great business won't be listed publicly.
          </h2>
          <p className="mx-auto mt-4 max-w-md text-base text-muted-foreground">
            {org
              ? `Join ${org.name}. See every listing, run the numbers, and get matched to deals that fit.`
              : "Free to join. See every listing, run the numbers, and get matched to deals that fit."}
          </p>
          <Link to={registerTo} className="mt-8 inline-block">
            <Button size="lg" className="shadow-glow">
              Register free account
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
